/**
 * Shared authentication utilities for cross-app communication
 * Used by both site/ and dash/ applications
 */

import { AUTH_CONFIG, DASH_URL, SITE_URL } from './config'

export interface AuthState {
  token: string
  user: {
    id: string
    name: string
    email: string
    avatar?: string
  }
  expiresAt: number
}

export interface AuthResult {
  success: boolean
  token?: string
  user?: AuthState['user']
  error?: string
}

/**
 * Store auth state that can be shared between site/ and dash/
 */
export function setAuthState(authState: AuthState): void {
  try {
    localStorage.setItem(AUTH_CONFIG.TOKEN_STORAGE_KEY, JSON.stringify(authState))
    localStorage.setItem(AUTH_CONFIG.SIMPLE_TOKEN_KEY, authState.token)
    console.log('[Auth] Stored auth state for cross-app use')
  } catch (error) {
    console.error('[Auth] Failed to store auth state:', error)
  }
}

/**
 * Get stored auth state from localStorage
 */
export function getAuthState(): AuthState | null {
  try {
    const stored = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY)
    if (!stored) return null

    const authState: AuthState = JSON.parse(stored)

    // Check if token is expired
    if (Date.now() > authState.expiresAt) {
      clearAuthState()
      return null
    }

    return authState
  } catch (error) {
    console.error('[Auth] Failed to parse stored auth state:', error)
    clearAuthState()
    return null
  }
}

/**
 * Get just the auth token (for API requests)
 */
export function getAuthToken(): string | null {
  try {
    const authState = getAuthState()
    return authState?.token || localStorage.getItem(AUTH_CONFIG.SIMPLE_TOKEN_KEY) || null
  } catch (error) {
    console.error('[Auth] Failed to get auth token:', error)
    return null
  }
}

/**
 * Clear all auth state
 */
export function clearAuthState(): void {
  try {
    localStorage.removeItem(AUTH_CONFIG.TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_CONFIG.SIMPLE_TOKEN_KEY)
    console.log('[Auth] Cleared auth state')
  } catch (error) {
    console.error('[Auth] Failed to clear auth state:', error)
  }
}

/**
 * Check if user is currently authenticated
 */
export function isAuthenticated(): boolean {
  const authState = getAuthState()
  return authState !== null && Date.now() < authState.expiresAt
}


/**
 * Navigate to dashboard with auth state
 * Can be used from site/ to redirect to dash/
 */
export function navigateToDashboard(baseUrl?: string): void {
  const authState = getAuthState()

  if (!authState) {
    console.warn('[Auth] No auth state available for dashboard navigation')
    window.location.href = `${baseUrl || SITE_URL}/login`
    return
  }

  // Use provided baseUrl or get from config
  const dashUrl = baseUrl || DASH_URL

  // Add auth token as URL parameter for dash/ to pick up
  const url = new URL(dashUrl)
  url.searchParams.set('auth_token', authState.token)
  url.searchParams.set('from_site', 'true')

  console.log('[Auth] Navigating to dashboard with auth state')
  window.location.href = url.toString()
}

/**
 * Check URL for auth token from cross-app navigation
 */
/**
 * Cookie utilities for test auth control
 */
export function setAuthSuppressCookie(suppress: boolean = true): void {
  try {
    if (suppress) {
      document.cookie = 'suppress_auto_auth=true; path=/; max-age=3600' // 1 hour
    } else {
      document.cookie = 'suppress_auto_auth=; path=/; max-age=0' // Clear cookie
    }
  } catch (error) {
    console.error('[Auth] Failed to set suppress cookie:', error)
  }
}

export function getAuthSuppressCookie(): boolean {
  try {
    return document.cookie
      .split(';')
      .some(cookie => cookie.trim().startsWith('suppress_auto_auth=true'))
  } catch (error) {
    console.error('[Auth] Failed to read suppress cookie:', error)
    return false
  }
}

export function clearAuthSuppressCookie(): void {
  setAuthSuppressCookie(false)
}

/**
 * Dev user switching utilities for testing
 */
export function setDevUserCookie(userId: string): void {
  try {
    if (!['dev1', 'dev2', 'dev3'].includes(userId)) {
      throw new Error('Invalid dev user ID. Must be dev1, dev2, or dev3')
    }
    document.cookie = `dev-user=${userId}; path=/; max-age=3600` // 1 hour
    console.log(`[Auth] Set dev user cookie: ${userId}`)
  } catch (error) {
    console.error('[Auth] Failed to set dev user cookie:', error)
  }
}

export function getDevUserCookie(): string | null {
  try {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'dev-user' && ['dev1', 'dev2', 'dev3'].includes(value)) {
        return value
      }
    }
    return null
  } catch (error) {
    console.error('[Auth] Failed to read dev user cookie:', error)
    return null
  }
}

export function clearDevUserCookie(): void {
  try {
    document.cookie = 'dev-user=; path=/; max-age=0' // Clear cookie
    console.log('[Auth] Cleared dev user cookie')
  } catch (error) {
    console.error('[Auth] Failed to clear dev user cookie:', error)
  }
}

export function checkForCrossAppAuth(): AuthState | null {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('auth_token')
    const fromSite = urlParams.get('from_site')

    if (token && fromSite) {
      // Clean up URL
      const url = new URL(window.location.href)
      url.searchParams.delete('auth_token')
      url.searchParams.delete('from_site')
      window.history.replaceState({}, document.title, url.toString())

      // Check if we have a full auth state in localStorage
      const storedAuth = getAuthState()
      if (storedAuth && storedAuth.token === token) {
        console.log('[Auth] Found valid cross-app auth state')
        return storedAuth
      }

      // If we only have the token, we don't have full user info
      // This should not happen with real JWTs - they contain user info
      console.warn('[Auth] Cross-app auth with token only (no stored auth state)')
      return null
    }

    return null
  } catch (error) {
    console.error('[Auth] Error checking for cross-app auth:', error)
    return null
  }
}

/**
 * Event listener for auth state changes across tabs/windows
 */
export function onAuthStateChange(callback: (authState: AuthState | null) => void): () => void {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === AUTH_CONFIG.TOKEN_STORAGE_KEY || event.key === AUTH_CONFIG.SIMPLE_TOKEN_KEY) {
      const newAuthState = getAuthState()
      callback(newAuthState)
    }
  }

  window.addEventListener('storage', handleStorageChange)

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange)
  }
}